import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Maximize, Eraser, Expand, Users, PaintBucket, Crop } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import ImageUploader from '../components/ImageUploader';

const quickActions = [
  { label: 'Basic Edit', desc: 'Crop, rotate, adjust', icon: <Crop size={22} />, path: '/edit' },
  { label: 'Upscale', desc: 'Enhance resolution', icon: <Maximize size={22} />, path: '/upscale' },
  { label: 'Remove', desc: 'Erase objects/people', icon: <Eraser size={22} />, path: '/remove' },
  { label: 'Extend', desc: 'Expand image borders', icon: <Expand size={22} />, path: '/extend' },
  { label: 'Variations', desc: 'Same person, new scenes', icon: <Users size={22} />, path: '/variations' },
  { label: 'Background', desc: 'Remove or swap bg', icon: <PaintBucket size={22} />, path: '/background' },
];

export default function HomePage() {
  const { images, addImage } = useLibrary();
  const navigate = useNavigate();

  const recentImages = useMemo(() => images.slice(0, 8), [images]);

  const handleUpload = useCallback(async (files: File[]) => {
    for (const file of files) {
      const image = await addImage(file, file.name);
      if (files.length === 1) {
        navigate(`/edit/${image.id}`);
      }
    }
  }, [addImage, navigate]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 sage-animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1
          className="text-2xl font-semibold tracking-tight"
          style={{ fontFamily: 'var(--sage-font-serif)', color: 'var(--sage-text)' }}
        >
          MEDA Studio
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--sage-text-muted)' }}>
          Your personal image editing suite
        </p>
      </div>

      {/* Upload area */}
      <ImageUploader onUpload={handleUpload} />

      {/* Quick Actions */}
      <div className="mt-10">
        <h2 className="text-sm font-medium uppercase tracking-wider mb-4" style={{ color: 'var(--sage-text-muted)' }}>
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickActions.map(action => (
            <button
              key={action.label}
              onClick={() => navigate(action.path)}
              className="flex flex-col items-center gap-2 p-4 rounded-xl transition-colors text-center"
              style={{ backgroundColor: 'var(--sage-surface)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--sage-surface-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--sage-surface)'}
            >
              <div style={{ color: 'var(--sage-primary)' }}>{action.icon}</div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--sage-text)' }}>{action.label}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--sage-text-muted)' }}>{action.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Images */}
      {recentImages.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium uppercase tracking-wider" style={{ color: 'var(--sage-text-muted)' }}>
              Recent Images
            </h2>
            <button
              onClick={() => navigate('/library')}
              className="text-xs transition-colors"
              style={{ color: 'var(--sage-primary)' }}
            >
              View all
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {recentImages.map(img => (
              <button
                key={img.id}
                onClick={() => navigate(`/edit/${img.id}`)}
                className="aspect-square rounded-xl overflow-hidden transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: 'var(--sage-surface)' }}
              >
                <img
                  src={URL.createObjectURL(img.thumbnail)}
                  alt={img.name}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
